import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import type { NetworkConnectionListItem } from '@kirin/api-client/contracts/shadow-ai';
import { NETWORK_CONNECTIONS_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/shadow-ai';
import {
  StringOperatorsWithEmpty,
  NumberOperatorsWithEmpty,
  DateOperatorsWithEmpty,
} from '@/components/shared/shadow-ai/operators';
import {
  MatchedEndpointChip,
  endpointFilterConfig,
} from '@/components/shared/shadow-ai';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { TruncatedText } from '@/components/shared/TruncatedText';
import { queryKeys } from '@/hooks/api/queryKeys';
import { formatBytes } from '../shared';
import { NETWORK_DOMAINS_FILTER_URL_KEYS } from './constants';
import { fetchNetworkConnectionsFilterOptions } from './api';
import {
  ConfidenceBadge,
  NetworkStatusBadge,
  NetworkActionsCell,
  type NetworkDomainsActions,
} from './CellRenderers';
import type { NetworkStatusFilterValue } from './NetworkStatusFilterSelector';
import { NETWORK_DOMAIN_COLUMN_WIDTHS } from './columnWidths';

/**
 * KIR-3496: Network Discovery domains table columns with per-column
 * Filter / Sort / Search, mirroring the Browser AI Tools table.
 *
 * `status` scopes the DYNAMIC filter dropdowns (Process/Host) so they only
 * offer values present in the current status view — matching the rows.
 *
 * KIR-4588: the Host leads the row. It is the thing the user reviews and
 * classifies, so it comes first, in bold, carrying the AI-confidence badge.
 * Process follows as muted supporting context — it is only the *modal*
 * process for the group and is not part of the ingest dedupe key, so it is
 * a hint about the traffic rather than a fact the user acts on.
 */
export function createNetworkDomainsColumns(
  organizationId: string,
  options: {
    showAgentColumn: boolean;
    endpointLabel: string;
    status: NetworkStatusFilterValue;
  }
): ColumnConfig<NetworkConnectionListItem>[] {
  const { showAgentColumn, endpointLabel, status } = options;

  const agentColumn: ColumnConfig<NetworkConnectionListItem>[] = showAgentColumn
    ? [
        {
          id: 'agent',
          header: 'Agent',
          accessor: 'identifiedAgentName',
          width: NETWORK_DOMAIN_COLUMN_WIDTHS.agent,
          renderCell: ({ row }) => (
            <TruncatedText className="font-medium">
              {row.identifiedAgentName || '-'}
            </TruncatedText>
          ),
        },
      ]
    : [];

  return [
    ...agentColumn,
    {
      id: 'domain',
      header: 'Host',
      accessor: 'domain',
      width: NETWORK_DOMAIN_COLUMN_WIDTHS.domain,
      renderCell: ({ row }) => (
        // KIR-5098: the matched-endpoint chip gets its own line under the
        // host, so it never squeezes the host name out of the cell.
        <div className="flex min-w-0 flex-col items-start gap-1">
          <div className="flex w-full min-w-0 items-center gap-2">
            <TruncatedText className="font-mono text-sm font-semibold text-foreground">
              {row.domain}
            </TruncatedText>
            {/*
            KIR-4838: with grouping on, this row is one service standing for
            several hosts, and the name alone does not say so.
            The count is what tells the reviewer to expand, so it sits in the
            Host cell rather than only in the expanded row. Hidden at 1 so the
            ungrouped page (and any single-host domain) is unchanged.
          */}
            {row.hostCount > 1 && (
              <span
                className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground"
                title={`${row.hostCount} hosts share this domain`}
              >
                {row.hostCount} hosts
              </span>
            )}
            <ConfidenceBadge connection={row} />
          </div>
          {/* KIR-5098: endpoint(s) matching an endpoint filter, on the closed row. */}
          <MatchedEndpointChip
            matchedEndpoints={row.matchedEndpoints}
            matchedEndpointCount={row.matchedEndpointCount}
          />
        </div>
      ),
      sortable: true,
      sortField: 'domain',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.HOST
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.shadowAi.networkConnectionsFilterOptions(
            organizationId,
            'domain',
            { ...params, status }
          ),
        queryFn: params =>
          fetchNetworkConnectionsFilterOptions(
            organizationId,
            'domain',
            status,
            params
          ),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: 'processName',
      header: 'Process',
      accessor: 'processName',
      width: NETWORK_DOMAIN_COLUMN_WIDTHS.processName,
      // Prefer the parent over the socket owner: KIR-4674 populates
      // `parentProcessName`, so browser-brokered agent traffic reads
      // "via Claude Code" rather than "via Google Chrome Helper". Falls back
      // to the socket owner until that data is present.
      renderCell: ({ row }) => {
        const driver = row.parentProcessName ?? row.processName;
        return (
          <TruncatedText className="text-sm text-muted-foreground">
            {driver ? `via ${driver}` : '-'}
          </TruncatedText>
        );
      },
      sortable: true,
      sortField: 'processName',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.PROCESS
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.shadowAi.networkConnectionsFilterOptions(
            organizationId,
            'processName',
            { ...params, status }
          ),
        queryFn: params =>
          fetchNetworkConnectionsFilterOptions(
            organizationId,
            'processName',
            status,
            params
          ),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      // Status is scoped by the top-level status selector, not a per-column
      // filter (KIR-3496) — this column is display + sort only. The AI
      // confidence hint moved off this cell onto the Host (KIR-4588).
      id: 'status',
      header: 'Status',
      accessor: 'status',
      width: NETWORK_DOMAIN_COLUMN_WIDTHS.status,
      renderCell: ({ row }) => <NetworkStatusBadge connection={row} />,
      sortable: true,
      sortField: 'status',
    },
    {
      id: 'totalBytes',
      header: 'Traffic',
      accessor: 'totalBytesIn',
      width: NETWORK_DOMAIN_COLUMN_WIDTHS.totalBytes,
      renderCell: ({ row }) => (
        <span className="font-mono text-sm">
          {formatBytes(row.totalBytesIn + row.totalBytesOut)}
        </span>
      ),
      sortable: true,
      sortField: 'totalBytes',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.TRAFFIC
          ],
        dataType: ColumnDataTypes.NUMBER,
      },
    },
    {
      id: 'hitCount',
      header: 'Hits',
      accessor: 'hitCount',
      width: NETWORK_DOMAIN_COLUMN_WIDTHS.hitCount,
      renderCell: ({ row }) => <span>{row.hitCount}</span>,
      sortable: true,
      sortField: 'hitCount',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.HITS
          ],
        dataType: ColumnDataTypes.NUMBER,
      },
    },
    {
      id: 'uniqueUsers',
      header: 'Users',
      // KIR-4750: a single-host row counts only its process's users, so it can
      // read lower than Machines and Hits, which count every process.
      description:
        "Users who reached the host through the row's process, matching the expanded list. A grouped service counts users across all its hosts. Traffic, Hits and Machines include every process.",
      accessor: 'uniqueUsers',
      width: NETWORK_DOMAIN_COLUMN_WIDTHS.uniqueUsers,
      renderCell: ({ row }) => <span>{row.uniqueUsers}</span>,
      sortable: true,
      sortField: 'uniqueUsers',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.USERS
          ],
        dataType: ColumnDataTypes.NUMBER,
      },
    },
    {
      // KIR-5098: still the distinct-endpoint count, but its filter now searches
      // endpoint name / ID (Contains, Equals), with the old count range kept as
      // the "Count range" entry in the same dropdown (alternate field).
      id: NETWORK_CONNECTIONS_FILTERABLE_FIELDS.ENDPOINT,
      header: endpointLabel,
      accessor: 'uniqueMachines',
      width: NETWORK_DOMAIN_COLUMN_WIDTHS.uniqueMachines,
      renderCell: ({ row }) => <span>{row.uniqueMachines}</span>,
      sortable: true,
      sortField: 'uniqueMachines',
      ...endpointFilterConfig({
        getQueryKey: params =>
          queryKeys.shadowAi.networkConnectionsFilterOptions(
            organizationId,
            'endpoint',
            { ...params, status }
          ),
        queryFn: params =>
          fetchNetworkConnectionsFilterOptions(
            organizationId,
            'endpoint',
            status,
            params
          ),
      }),
      alternateFilter: {
        field: NETWORK_CONNECTIONS_FILTERABLE_FIELDS.MACHINES,
        label: 'Count range',
        dataType: ColumnDataTypes.NUMBER,
        operator: FilterOperators.INCLUDES,
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.MACHINES
          ],
      },
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.ENDPOINT
          ],
      },
    },
    {
      id: 'lastSeen',
      header: 'Last Seen',
      timestamp: true,
      accessor: 'lastSeen',
      renderCell: ({ row }) => (
        <span className="text-xs text-muted-foreground">
          <RelativeTimeWithTooltip date={row.lastSeen} />
        </span>
      ),
      sortable: true,
      sortField: 'lastSeen',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.LAST_SEEN
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
}

/** Trailing Actions column (Identify / Dismiss / Revert). Not sortable/filterable. */
export function createNetworkDomainsActionsColumn(
  actions: NetworkDomainsActions
): ColumnConfig<NetworkConnectionListItem> {
  return {
    id: 'actions',
    header: 'Actions',
    // Action buttons must stay fully visible.
    clipOverflow: false,
    accessor: 'domain',
    width: NETWORK_DOMAIN_COLUMN_WIDTHS.actions,
    renderCell: ({ row }) => <NetworkActionsCell row={row} actions={actions} />,
  };
}
